import {
  Box,
  Stack,
  Text,
  Button,
  Heading,
  SimpleGrid,
  StackDivider,
  List,
  ListItem,
} from '@chakra-ui/react';
import { TriggerOverviewMetadata, UserSummary } from 'services/hitchit/types/admin/Summary';
import { Hitch } from 'services/hitchit/types/hitch/Hitch';

function TriggerOverviewMetaData({
  triggerOverviewMetadata,
}: {
  triggerOverviewMetadata: TriggerOverviewMetadata;
}) {
  return (
    <>
      <ListItem>{`Triggers: ${triggerOverviewMetadata.triggers}`}</ListItem>
      {triggerOverviewMetadata.triggers > 0 && (
        <>
          <ListItem>{`Shopify Orders: ${triggerOverviewMetadata.shopify?.orders}`}</ListItem>
          <ListItem>{`Teamleader Resources: ${JSON.stringify(
            triggerOverviewMetadata.teamleader || {}
          )}`}</ListItem>
          <ListItem>{`Status: ${JSON.stringify(triggerOverviewMetadata.status || {})}`}</ListItem>
          <ListItem>{`Type: ${JSON.stringify(triggerOverviewMetadata.type || {})}`}</ListItem>
        </>
      )}
    </>
  );
}

export function UserDetails({
  userSummary: { user, hikes, hitches },
}: {
  userSummary: UserSummary;
}): JSX.Element {
  // TODO move this mapping to backend
  const { email, uid } = user;
  const baseHikes = hikes?.map(
    ({ useCaseId, id, hitchRef, billingId, status, preferences, triggerInfo }) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const hitchDetails = hitches?.find(
        ({ hitchRef: daHitchRef }) => daHitchRef === hitchRef
      ) as Hitch;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { credentials, ...hitch } = hitchDetails || { credentials: {} };

      return {
        base: {
          useCaseId,
          id,
          hitchRef,
          billingId,
          status,
          hitch,
          triggerInfo,
          department: preferences?.['dealsSupported.true.department_id'] as { label: string },
        },
        preferences,
      };
    }
  );
  const baseUser = { email, uid, baseHikes };
  return (
    <Stack spacing={{ base: 6, md: 10 }}>
      <Box as={'header'}>
        <Heading lineHeight={1.1} fontWeight={600} fontSize={'2xl'}>
          {baseUser.email}
        </Heading>
        <Text color={'gray.900'} fontWeight={300} fontSize={'2xl'}>
          {baseUser.baseHikes?.find(({ base: { status } }) => status === 'active')
            ? 'active'
            : baseUser.baseHikes?.map(({ base: { status } }) => `${status}`).join(',')}
        </Text>
      </Box>

      {baseUser.baseHikes?.map(({ base }) => (
        <Stack
          key={base.id}
          spacing={{ base: 4, sm: 6 }}
          direction={'column'}
          divider={<StackDivider borderColor={'gray.200'} />}
        >
          <Box>
            <Text
              fontSize={{ base: '16px', lg: '18px' }}
              color={'yellow.500'}
              fontWeight={'500'}
              mb={'4'}
            >
              {`Hike ${base.id}`}
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
              <List spacing={2}>
                <ListItem>{base.status}</ListItem>
                <ListItem>{base.useCaseId}</ListItem>
                <ListItem>{base.department?.label}</ListItem>
              </List>
              <List spacing={2}>
                <ListItem>
                  <Text as={'span'} fontWeight={'bold'}>
                    Triggers
                  </Text>{' '}
                </ListItem>
                <ListItem>{`Allowed: ${base.triggerInfo.totalTriggersAllowed}`}</ListItem>
                <ListItem>
                  <Text as={'span'} fontWeight={'bold'}>
                    This month
                  </Text>{' '}
                </ListItem>
                <TriggerOverviewMetaData
                  triggerOverviewMetadata={base.triggerInfo.totalTriggersThisMonth}
                />
                <ListItem>
                  <Text as={'span'} fontWeight={'bold'}>
                    Previous month
                  </Text>{' '}
                </ListItem>
                <TriggerOverviewMetaData
                  triggerOverviewMetadata={base.triggerInfo.totalTriggersPreviousMonth}
                />
              </List>
            </SimpleGrid>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            {base.hitch.users &&
              Object.values(base.hitch.users).map(({ appUrl, appName, displayName, shopName }) => (
                <Box key={appName}>
                  <Text
                    fontSize={{ base: '16px', lg: '18px' }}
                    color={'yellow.500'}
                    fontWeight={'500'}
                    textTransform={'uppercase'}
                    mb={'4'}
                  >
                    {appName}
                  </Text>

                  <List spacing={2}>
                    <ListItem>
                      <Text as={'span'} fontWeight={'bold'}>
                        appName
                      </Text>{' '}
                      {appName}
                    </ListItem>
                    <ListItem>
                      <Text as={'span'} fontWeight={'bold'}>
                        appUrl:
                      </Text>{' '}
                      {appUrl}
                    </ListItem>
                    <ListItem>
                      <Text as={'span'} fontWeight={'bold'}>
                        displayName:
                      </Text>{' '}
                      {displayName}
                    </ListItem>
                    <ListItem>
                      <Text as={'span'} fontWeight={'bold'}>
                        shopName:
                      </Text>{' '}
                      {shopName}
                    </ListItem>
                  </List>
                </Box>
              ))}
          </SimpleGrid>
        </Stack>
      ))}

      <Button
        rounded={'none'}
        w={'full'}
        mt={8}
        size={'lg'}
        py={'7'}
        bg={'gray.900'}
        color="white"
        textTransform={'uppercase'}
        _hover={{
          transform: 'translateY(2px)',
          boxShadow: 'lg',
        }}
      >
        Preference details
      </Button>
      <Button
        rounded={'none'}
        w={'full'}
        mt={8}
        size={'lg'}
        py={'7'}
        bg={'gray.900'}
        color="white"
        textTransform={'uppercase'}
        _hover={{
          transform: 'translateY(2px)',
          boxShadow: 'lg',
        }}
      >
        Billing details
      </Button>
    </Stack>
  );
}
